import React, { useState } from "react";
import { Form, Stack } from "react-bootstrap";
import { useQueryClient } from "@tanstack/react-query";
import { useAtom } from "jotai";

import "./DobModal.scss";
import BottomDrawer from "../BottomDrawer/BottomDrawer";
import PrimaryActionButton from "../Buttons/PrimaryActionButton/PrimaryActionButton";
import WarningBadge from "../WarningBadge/WarningBadge";
import useDisclosure from "../../hooks/useDisclosure";
import DatePicker, { DateValue } from "../DatePicker/DatePicker";
import { convertToISODate, isUpToLegalAge } from "../../utils/helpers";
import { handleUpdateUser } from "../../api-clients/userAccountClient";
import { showSuccessToast } from "../../utils/show-toast";
import { CustomSpinner } from "../CustomSpinner/CustomSpinner";
import { GenericModalProps } from "../../utils/constants/generic-types";
import { userProfileAtom } from "../../jotai/atoms/user";
import { QUERY_KEYS } from "../../react-query/constants";
import { useTranslation } from "react-i18next";
import useAnalytics from "../../hooks/useAnalytics";

interface DobModalProps extends GenericModalProps {
  handleSuccess: (updatedDob: string) => void;
}

const defaultDob = {
  day: "",
  month: "",
  year: "",
};

const formatDateAsReadableStr = (date: DateValue) => {
  return `${date.month.slice(0, 3)} ${date.day}, ${date.year}`;
};

export default function DobModal({
  isOpen,
  onClose,
  handleSuccess,
}: DobModalProps) {
  const analytics = useAnalytics();
  const queryClient = useQueryClient();
  const [userProfile, setUserProfile] = useAtom(userProfileAtom);
  const [loading, setLoading] = useState<boolean>(false);
  const [dob, setDob] = useState<DateValue>(defaultDob);
  const [isLegalAge, setIsLegalAge] = useState<boolean>(true);
  const dobPartIsMissing = Object.values(dob).includes("");
  const { t: tToasts } = useTranslation("toasts");
  const { t: tCommon } = useTranslation("common", {
    keyPrefix: "dob_modal",
  });
  const {
    isOpen: isDatePickerOpen,
    onOpen: openDatePicker,
    onClose: closeDatePicker,
  } = useDisclosure();

  const handleDateSelection = (date: DateValue) => {
    const dateAsReadableStr = formatDateAsReadableStr(date);
    setIsLegalAge(isUpToLegalAge(dateAsReadableStr));
    setDob(date);
  };

  const handleDobSubmission = async () => {
    setLoading(true);

    try {
      const dobInISOFormat = convertToISODate(formatDateAsReadableStr(dob));
      const userData = await handleUpdateUser({ dob: dobInISOFormat });
      if (userData) {
        analytics.sendSelectDobEvent({
          date_of_birth_selected: dobInISOFormat,
          user_currency: userProfile?.currency,
        });
        showSuccessToast(tToasts("dob_updated"));
        queryClient.refetchQueries({
          queryKey: [QUERY_KEYS.USER.VERIFICATION_STATUS],
        });
        handleSuccess(dobInISOFormat);
        setUserProfile(userData);
        setDob(defaultDob);
        onClose();
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <BottomDrawer isOpen={isOpen} onClose={onClose} containerClass="DobModal">
        <div className="pt-2 d-flex flex-column justify-content-between ModalContent">
          <Stack gap={4}>
            <Stack gap={1} className="Copy">
              <h1 className="Hanson-700 Heading">
                {tCommon("what_is_your_dob")}
              </h1>
              <p className="Grotesk SubCopy">
                {tCommon("you_must_be_18+")} {tCommon("to_play_games_on_hyper")}
              </p>
              <WarningBadge
                text={tCommon("we'll_confirm_submission_during_verification")}
              />
            </Stack>

            <Form id="DobForm">
              <Form.Group controlId="dob">
                <Form.Label className="mb-2 Grotesk SpecialFontSettings DobLabel">
                  {tCommon("dob")}
                </Form.Label>
                <Form.Control
                  size="lg"
                  readOnly
                  value={dobPartIsMissing ? "" : formatDateAsReadableStr(dob)}
                  placeholder="MMM/DD/YYYY"
                  className="Grotesk SpecialFontSettings DobInput"
                  onClick={openDatePicker}
                />
                {!isLegalAge && (
                  <p className="mt-2 Grotesk DobErrorText">
                    {tCommon("you_must_be_18+")}
                  </p>
                )}
              </Form.Group>
            </Form>
          </Stack>

          <PrimaryActionButton
            text={!loading ? "Continue to Top Up" : <CustomSpinner size="sm" />}
            form="DobForm"
            disabled={dobPartIsMissing || !isLegalAge || loading}
            onClick={handleDobSubmission}
          />
        </div>
      </BottomDrawer>

      <DatePicker
        isOpen={isDatePickerOpen}
        onClose={closeDatePicker}
        initialDateValue={dob}
        handleDateSelection={handleDateSelection}
      />
    </>
  );
}
